import { ServiceData, SlotService } from '../../../../../utils/state/types';
import { CartFlow, CartModalStatus } from '../../../../../types/types';
import { FormValues } from '@wix/form-viewer';
import {
  BookingsQueryParams,
  IFlowApiAdapter,
} from '../../../../../utils/flow-api-adapter/types';
import { CreateActionParams } from '../../actions';
import {
  bookingsUouBookFlowBookingFormClickNext,
  bookingsUouBookFlowBookingFormClickNextFailure,
  bookingsUouSessionAddedToCartModalInteractions,
} from '@wix/bi-logger-wixboost-ugc/v2';
import { AddToCartErrorType, BookErrorType } from '../../../../../types/errors';
import {
  CreateCheckoutResponse,
  CreateOrderResponse,
} from '@wix/ambassador-ecom-v1-checkout/types';
import { FormStatus } from '../../../../../types/form-state';
import { mapCartToBookingsLineItems } from '../../../../../utils/mappers/cart.mapper';
import {
  AddToCartResponse,
  Cart,
  UpdateCartRequest,
} from '@wix/ambassador-ecom-v1-cart/types';
import { FormApi } from '../../../../../api/FormApi';
import {
  CartModalInteractionActionName,
  FormClickNextAction,
} from '../../../../../types/biLoggerTypes';
import { Experiments } from '@wix/yoshi-flow-editor';
import { canCompleteBoooking } from '../../../../../utils/validations/canBook';
import { ContactDetails } from '../../../../../types/ambassador/bookings/ambassador-bookings-v2-booking';
import {
  FormBiLogger,
  extraInfoMapper,
  getDynamicPriceBIReportParams,
} from '../../../../../utils/bi/biLoggerFactory';
import { getServiceSlotIdentifier, mapToArray } from '../../../../../utils';
import { getErrorByType } from '../../../../../utils/errors/errors';
import { PanoramaEventNames } from '../../../../../utils/panorama/panorama';

export async function handleBookFlow(
  {
    getControllerState,
    internalActions: { errorHandlers },
    context: {
      biLogger,
      wixSdkAdapter,
      formApi,
      reportError,
      experiments,
      panoramaLogger,
    },
  }: CreateActionParams,
  wixFormValues: FormValues,
  cartFlow?: CartFlow,
) {
  const [state, setState] = getControllerState();
  const {
    businessInfo,
    couponInfo,
    isCart,
    isDayful,
    serviceData,
    isUpsellPluginInstalled,
    selectedNumberOfParticipants,
    memberDetails,
  } = state;

  if (
    !(await canCompleteBoooking({
      formApi,
      isDayful: isDayful!,
      wixSdkAdapter,
      serviceData,
      activeFeatures: businessInfo.activeFeatures!,
    }))
  ) {
    await wixSdkAdapter.openUoUPremiumModal(
      // All the services are the same type so we can take the first one
      mapToArray<SlotService>(serviceData.slotServices)[0].service.type,
      'bookings-form',
      isDayful,
    );
    setState({ status: FormStatus.IDLE });
    return;
  }
  if (isCart) {
    panoramaLogger?.transactionStarted(PanoramaEventNames.AddSessionToCart);
  } else {
    panoramaLogger?.transactionStarted(PanoramaEventNames.NavigateToCheckout);
  }

  try {
    const numberOfParticipants = selectedNumberOfParticipants || 1;

    const {
      contactDetails,
      createCheckoutResponse,
      bookingIds,
      addToCurrentCartResponse,
    } = await formApi.checkoutBooking({
      memberDetails,
      serviceData,
      wixFormValues,
      numberOfParticipants,
      isCart,
      cartFlow,
      onError: (error, details) => {
        errorHandlers.addError(error, {
          numberOfParticipants,
          message: details,
        });
        biLogger?.report(
          bookingsUouBookFlowBookingFormClickNextFailure({
            errorReason: error,
          }),
        );
      },
      country: businessInfo.countryCode!,
      couponCheckboxChecked: couponInfo.isCouponCheckboxChecked,
    });

    const addToCartError = getErrorByType({
      errorType: AddToCartErrorType,
      errors: state.errors,
    });

    if (
      addToCartError?.errorType ===
        AddToCartErrorType.CART_IS_ALREADY_ASSOCIATED_WITH_DIFFERENT_LOCATION ||
      (isCart && addToCurrentCartResponse && !addToCurrentCartResponse?.cart)
    ) {
      setState({ status: FormStatus.IDLE });
      return;
    } else {
      if (
        createCheckoutResponse &&
        bookingIds &&
        !isSubmitValid(createCheckoutResponse, bookingIds)
      ) {
        setState({ status: FormStatus.IDLE });
        return;
      }

      await reportBiForBookingsUouBookFlowBookingFormClickNext({
        serviceData,
        bookingIds,
        biLogger,
        isCart,
        numberOfParticipants,
        wixSdkAdapter,
      });
    }

    let continueShoppingUrl = '';

    const queryParams = wixSdkAdapter.getUrlQueryParams();
    if (isDayful) {
      continueShoppingUrl =
        queryParams.origin ||
        `${await wixSdkAdapter.getBookOnlineFullUrl()}?dayful=${
          queryParams.dayful
        }`;
    }

    if (addToCurrentCartResponse?.cart) {
      const lineItems = addToCurrentCartResponse?.cart?.lineItems || [];
      if (lineItems.length === 1) {
        await updateCartWithMemberDetails({
          addToCurrentCartResponse,
          contactDetails,
          formApi,
          country: businessInfo.countryCode!,
          experiments,
        });
      }
      await wixSdkAdapter.reloadCart();
      if (
        wixSdkAdapter.getUrlQueryParamValue(
          BookingsQueryParams.CONTINUE_SHOPPING_URL,
        ) ||
        isUpsellPluginInstalled
      ) {
        setState({
          cartModal: {
            status: CartModalStatus.OPEN,
            bookingIds: Object.values(bookingIds || {}),
            lineItems,
            biLoggerData: getBiLoggerData({
              serviceData,
              bookingIds,
              wixSdkAdapter,
            }),
          },
          status: FormStatus.IDLE,
        });
        biLogger?.report(
          bookingsUouSessionAddedToCartModalInteractions({
            ...getBiLoggerData({
              serviceData,
              bookingIds,
              wixSdkAdapter,
            }),
            actionName: CartModalInteractionActionName.Load,
          }),
        );
        panoramaLogger?.transactionEnded(PanoramaEventNames.AddSessionToCart);
      } else {
        panoramaLogger?.transactionEnded(PanoramaEventNames.AddSessionToCart);
        await wixSdkAdapter.navigateAfterAddToCart(true);
      }
    } else {
      panoramaLogger?.transactionEnded(PanoramaEventNames.NavigateToCheckout);
      if (createCheckoutResponse && isOnlineFlow(createCheckoutResponse)) {
        return wixSdkAdapter.navigateToEcomCheckoutPage({
          checkoutId: createCheckoutResponse!.checkout!.id!,
          ...(continueShoppingUrl ? { continueShoppingUrl } : {}),
        });
      } else {
        return wixSdkAdapter.navigateToEcomThankYouPage({
          orderId: createCheckoutResponse!.orderId!,
          ...(continueShoppingUrl ? { continueShoppingUrl } : {}),
        });
      }
    }
  } catch (error) {
    errorHandlers.addError(error as BookErrorType);
    reportError(error as BookErrorType);
    setState({ status: FormStatus.IDLE });
    biLogger?.report(
      bookingsUouBookFlowBookingFormClickNextFailure({
        errorReason: error as Maybe<string>,
      }),
    );
  }
}

const isSubmitValid = (
  createCheckoutResponse: CreateCheckoutResponse | CreateOrderResponse,
  bookingIds: { [key: string]: string },
) => {
  if (!Object.keys(bookingIds).length) {
    return false;
  }

  if (isOnlineFlow(createCheckoutResponse)) {
    return createCheckoutResponse.checkout?.id !== undefined;
  }

  return createCheckoutResponse?.orderId !== undefined;
};

const getBiLoggerData = ({
  serviceData,
  bookingIds,
  numberOfParticipants,
  wixSdkAdapter,
}: {
  serviceData: ServiceData;
  bookingIds: { [key: string]: string } | undefined;
  numberOfParticipants?: number;
  wixSdkAdapter?: IFlowApiAdapter;
}) => {
  const firstService = mapToArray<SlotService>(serviceData.slotServices)[0];
  let dynamicPriceBIReportParams;
  if (firstService.dynamicPriceInfo?.serviceOptionsAndVariants) {
    dynamicPriceBIReportParams = getDynamicPriceBIReportParams(
      firstService.dynamicPriceInfo!,
      true,
    );
  }

  const bookingId =
    bookingIds?.[getServiceSlotIdentifier(firstService.nestedSlot)];

  const addOnIds = firstService.selectedAddons
    ? Object.keys(firstService.selectedAddons)
    : undefined;

  return {
    smsNotificationRequest: true,
    extraInfo: extraInfoMapper({
      serviceData,
      numberOfParticipants: numberOfParticipants || 1,
      bookingIds,
      withDynamicPrice: true,
      wixSdkAdapter: wixSdkAdapter!,
    }),
    ...(bookingId ? { bookingId } : {}),
    ...(dynamicPriceBIReportParams ? dynamicPriceBIReportParams : {}),
    ...(addOnIds?.length ? { addOnIds: addOnIds.join(',') } : {}),
    numberOfServices: mapToArray<SlotService>(serviceData.slotServices).length,
  };
};

async function reportBiForBookingsUouBookFlowBookingFormClickNext({
  serviceData,
  bookingIds,
  biLogger,
  isCart,
  numberOfParticipants,
  wixSdkAdapter,
}: {
  serviceData: ServiceData;
  bookingIds: { [key: string]: string } | undefined;
  biLogger: FormBiLogger | undefined;
  isCart: boolean | undefined;
  numberOfParticipants?: number;
  wixSdkAdapter?: IFlowApiAdapter;
}) {
  await biLogger?.report(
    bookingsUouBookFlowBookingFormClickNext({
      ...getBiLoggerData({
        serviceData,
        bookingIds,
        numberOfParticipants,
        wixSdkAdapter,
      }),
      ...(isCart ? { action: FormClickNextAction.AddToCart } : {}),
    }),
  );
}

function isOnlineFlow(
  checkoutResponse: CreateCheckoutResponse | CreateOrderResponse,
): checkoutResponse is CreateCheckoutResponse {
  return (checkoutResponse as CreateCheckoutResponse)?.checkout !== undefined;
}

async function updateCartWithMemberDetails({
  addToCurrentCartResponse,
  contactDetails,
  formApi,
  country,
}: {
  addToCurrentCartResponse?: AddToCartResponse;
  contactDetails: ContactDetails;
  formApi: FormApi;
  country: string;
  experiments: Experiments;
}) {
  const cartLineItems = addToCurrentCartResponse?.cart?.lineItems || [];
  const bookingsLineItems = mapCartToBookingsLineItems(cartLineItems);
  const isFirstBookingItemOnCart = bookingsLineItems.length === 1;

  if (isFirstBookingItemOnCart) {
    const cartInfo: Cart = {
      id: addToCurrentCartResponse?.cart?.id,
      buyerInfo: {
        email: contactDetails.email ?? '',
      },
      contactInfo: {
        ...(contactDetails.fullAddress && country
          ? { address: { ...contactDetails.fullAddress, country } }
          : {}),
        contactDetails: formApi.mapContactDetails({ contactDetails }),
      },
    };
    const updateCartRequest: UpdateCartRequest = {
      cartInfo,
    };
    await formApi.updateCart(updateCartRequest);
  }
}
